import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { AnimatedNumber } from '@/components/viz/Kpi'
import { niceScale, formatDate, shortDay, useWidth } from '@/components/viz/utils'
import { useGardenOperations, useGraphOverview, useKnowledgeHealth } from '../hooks/useKnowledge'
import { categoryColor } from './categoryStyle'
import { compactCount, dailySnapshots, decisionDays } from './gardenCharts'

const measures = [
  { key: 'concepts', label: 'Concepts', color: 'hsl(var(--arthaus-gold))' },
  { key: 'artworkLinks', label: 'Artwork links', color: 'var(--viz-active)' },
  { key: 'relationsTotal', label: 'Curated relationships', color: 'var(--viz-sessions)' },
] as const

export function KnowledgeVisualOverview() {
  const [days, setDays] = useState(7)
  const [measure, setMeasure] = useState<(typeof measures)[number]>(measures[0])
  const [point, setPoint] = useState<number | null>(null)
  const [activity, setActivity] = useState<number | null>(null)
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [category, setCategory] = useState<string | null>(null)
  const health = useKnowledgeHealth(30)
  const operations = useGardenOperations(days)
  const overview = useGraphOverview()
  const [chartRef, measuredWidth] = useWidth<HTMLDivElement>()
  const chartWidth = Math.max(300, measuredWidth)
  const plotWidth = chartWidth - 94
  const gradient = useId().replace(/:/g, '')
  const history = dailySnapshots(health.data?.history ?? [], days)
  const selected = point === null ? null : history[Math.min(point, history.length - 1)]
  const latest = health.data?.now[measure.key]
  const value = selected?.metrics[measure.key] ?? latest
  const scale = niceScale(Math.max(0, ...history.map((p) => p.metrics[measure.key])))
  const x = (i: number) =>
    history.length > 1 ? 54 + (i / (history.length - 1)) * plotWidth : 54 + plotWidth / 2
  const y = (v: number) => 220 - (v / scale.max) * 186
  const line = history
    .map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.metrics[measure.key])}`)
    .join(' ')
  const area = history.length > 1 ? `${line} L${x(history.length - 1)},220 L54,220Z` : ''
  const daily = operations.data
    ? decisionDays(operations.data.daily, days, operations.data.observedAt)
    : []
  const activityMax = Math.max(1, ...daily.map((d) => Math.max(d.proposed, d.person + d.autopilot)))
  const activeDay = activity === null ? null : daily[activity]
  const categories = [...(overview.data?.categories ?? [])].sort((a, b) => b.concepts - a.concepts)
  const total = categories.reduce((n, c) => n + c.concepts, 0)
  const chosen = categories.find((c) => c.category === category)
  let offset = 0

  return (
    <section aria-label="Knowledge visual overview" className="space-y-5">
      <div className="viz-rise relative overflow-hidden rounded-2xl border bg-card p-5 md:p-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 90% 0%, hsl(var(--arthaus-gold) / .12), transparent 55%)',
          }}
        />
        <div className="relative flex flex-wrap justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[.18em] text-muted-foreground">
              The garden, taking shape
            </p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl">
              A growing world of understanding.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Explore the graph&apos;s growth, the work behind it, and the ideas it connects.
            </p>
          </div>
          <div
            role="group"
            aria-label="Chart period"
            className="flex h-fit rounded-lg border bg-background p-1"
          >
            {[7, 30].map((n) => (
              <button
                key={n}
                aria-pressed={days === n}
                onClick={() => {
                  setDays(n)
                  setPoint(null)
                  setActivity(null)
                }}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors ${days === n ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {n} days
              </button>
            ))}
          </div>
        </div>
        {health.isError ? (
          <p role="alert" className="mt-6 text-sm">
            Growth history could not be loaded.{' '}
            <button className="underline" onClick={() => health.refetch()}>
              Retry
            </button>
          </p>
        ) : (
          <>
            <div className="relative mt-7 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  {measure.label} ·{' '}
                  {selected ? formatDate(selected.takenAt, true) : 'current graph'}
                </p>
                <p
                  className="mt-1 font-serif text-5xl font-semibold tracking-tight"
                  aria-live="polite"
                >
                  {value === undefined ? '—' : <AnimatedNumber value={value} />}
                </p>
              </div>
              <div role="group" aria-label="Graph measure" className="flex flex-wrap gap-2">
                {measures.map((m) => (
                  <button
                    key={m.key}
                    aria-pressed={measure.key === m.key}
                    onClick={() => {
                      setMeasure(m)
                      setPoint(null)
                    }}
                    className={`flex items-center gap-2 rounded-full border px-3 py-2 text-xs transition-colors ${measure.key === m.key ? 'border-foreground bg-background text-foreground' : 'border-transparent text-muted-foreground hover:bg-muted'}`}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: m.color }} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div ref={chartRef}>
              {history.length ? (
                <svg
                  viewBox={`0 0 ${chartWidth} 268`}
                  className="relative mt-3 w-full rounded-lg focus-visible:outline focus-visible:outline-2"
                  role="img"
                  tabIndex={0}
                  aria-label={`${measure.label} history. Use left and right arrows to inspect daily observations; Escape to reset.`}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setPoint(null)
                    else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                      e.preventDefault()
                      setPoint((p) =>
                        Math.max(
                          0,
                          Math.min(
                            history.length - 1,
                            (p ?? history.length - 1) + (e.key === 'ArrowRight' ? 1 : -1)
                          )
                        )
                      )
                    }
                  }}
                  onBlur={() => setPoint(null)}
                  onPointerLeave={() => setPoint(null)}
                >
                  <defs>
                    <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
                      <stop stopColor={measure.color} stopOpacity=".26" />
                      <stop offset="1" stopColor={measure.color} stopOpacity=".015" />
                    </linearGradient>
                  </defs>
                  {scale.ticks.map((t) => (
                    <g key={t}>
                      <line
                        x1="54"
                        x2={chartWidth - 40}
                        y1={y(t)}
                        y2={y(t)}
                        stroke="currentColor"
                        className="text-border"
                        strokeDasharray="3 6"
                      />
                      <text
                        x="43"
                        y={y(t) + 4}
                        textAnchor="end"
                        fill="currentColor"
                        className="text-muted-foreground"
                        fontSize="11"
                      >
                        {compactCount(t)}
                      </text>
                    </g>
                  ))}
                  <g key={`${measure.key}-${days}`}>
                    <path d={area} fill={`url(#${gradient})`} className="viz-fade" />
                    <path
                      d={line}
                      stroke={measure.color}
                      strokeWidth="3"
                      fill="none"
                      pathLength="1"
                      className="viz-draw"
                    />
                    {history.map((p, i) => (
                      <g key={p.takenAt}>
                        {(i === 0 ||
                          i === history.length - 1 ||
                          (chartWidth < 600
                            ? i === Math.floor((history.length - 1) / 2)
                            : i % Math.ceil(history.length / 6) === 0)) && (
                          <text
                            x={x(i)}
                            y="250"
                            textAnchor="middle"
                            fill="currentColor"
                            className="text-muted-foreground"
                            fontSize="11"
                          >
                            {formatDate(p.takenAt).replace(/, \d{4}$/, '')}
                          </text>
                        )}
                        {point === i && (
                          <line
                            x1={x(i)}
                            x2={x(i)}
                            y1="24"
                            y2="220"
                            stroke={measure.color}
                            strokeDasharray="3 4"
                          />
                        )}
                        <circle
                          cx={x(i)}
                          cy={y(p.metrics[measure.key])}
                          r={point === i ? 6 : 3.5}
                          fill={measure.color}
                          stroke="hsl(var(--card))"
                          strokeWidth="2"
                        />
                        <rect
                          x={
                            x(i) -
                            (history.length > 1
                              ? plotWidth / 2 / (history.length - 1)
                              : plotWidth / 2)
                          }
                          y="20"
                          width={history.length > 1 ? plotWidth / (history.length - 1) : plotWidth}
                          height="204"
                          fill="transparent"
                          onPointerEnter={() => setPoint(i)}
                        >
                          <title>
                            {formatDate(p.takenAt)}: {p.metrics[measure.key].toLocaleString()}{' '}
                            {measure.label.toLowerCase()}
                          </title>
                        </rect>
                      </g>
                    ))}
                  </g>
                </svg>
              ) : (
                <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
                  {health.isLoading
                    ? 'Loading graph history…'
                    : 'No snapshots in this period. The current graph is shown above.'}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Last recorded snapshot per day · Chicago time ·{' '}
              {history.length === 1
                ? 'One observation so far; more builds will reveal the trend.'
                : 'Hover or use arrow keys to inspect observations. Lines connect recorded days.'}
            </p>
          </>
        )}
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <div
          className="viz-rise rounded-2xl border bg-card p-5 md:p-6"
          style={{ animationDelay: '100ms' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-serif text-2xl">The work behind the growth</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Proposals arriving and decisions accepted · {days} days
              </p>
            </div>
            <Link
              to="/app/knowledge/agent"
              className="rounded p-1 hover:bg-muted"
              aria-label="Open resolution agent"
            >
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            {[
              ['Proposed', 'hsl(var(--arthaus-gold))'],
              ['Autopilot', 'var(--viz-active)'],
              ['People', 'var(--viz-sessions)'],
            ].map(([label, color]) => (
              <span key={label} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm" style={{ background: color }} />
                {label}
              </span>
            ))}
          </div>
          {operations.isError ? (
            <p role="alert" className="mt-8 text-sm">
              Decision activity could not be loaded.{' '}
              <button className="underline" onClick={() => operations.refetch()}>
                Retry
              </button>
            </p>
          ) : (
            <>
              <div className="mt-4 min-h-10 text-sm" aria-live="polite">
                {activeDay ? (
                  <>
                    <strong>{shortDay(activeDay.day)}</strong> ·{' '}
                    {activeDay.proposed.toLocaleString()} proposed ·{' '}
                    {activeDay.autopilot.toLocaleString()} autopilot ·{' '}
                    {activeDay.person.toLocaleString()} by people
                  </>
                ) : (
                  <>
                    <span className="font-serif text-3xl">
                      <AnimatedNumber value={operations.data?.totals.acceptedWindow ?? 0} />
                    </span>{' '}
                    <span className="text-muted-foreground">accepted decisions in this period</span>
                  </>
                )}
              </div>
              {daily.length ? (
                <svg
                  viewBox="0 0 500 185"
                  className="mt-2 w-full"
                  role="img"
                  aria-label="Daily proposed and accepted decisions. Each pair compares proposals with stacked autopilot and human acceptances."
                >
                  {[0, 0.5, 1].map((t) => (
                    <g key={t}>
                      <line
                        x1="36"
                        x2="490"
                        y1={143 - t * 125}
                        y2={143 - t * 125}
                        stroke="currentColor"
                        className="text-border"
                        strokeDasharray="3 5"
                      />
                      <text
                        x="30"
                        y={147 - t * 125}
                        textAnchor="end"
                        fontSize="10"
                        fill="currentColor"
                        className="text-muted-foreground"
                      >
                        {compactCount(activityMax * t)}
                      </text>
                    </g>
                  ))}
                  {daily.map((d, i) => {
                    const slot = 448 / daily.length
                    const bx = 40 + i * slot
                    const bw = Math.min(15, slot * 0.32)
                    return (
                      <g
                        key={d.day}
                        onPointerEnter={() => setActivity(i)}
                        onClick={() => setActivity(i)}
                      >
                        <g className="kg-bar-rise" style={{ animationDelay: `${i * 15}ms` }}>
                          <rect
                            x={bx}
                            y={143 - (d.proposed / activityMax) * 125}
                            width={bw}
                            height={(d.proposed / activityMax) * 125}
                            rx="2"
                            fill="hsl(var(--arthaus-gold))"
                          />
                          <rect
                            x={bx + bw + 2}
                            y={143 - (d.autopilot / activityMax) * 125}
                            width={bw}
                            height={(d.autopilot / activityMax) * 125}
                            fill="var(--viz-active)"
                          />
                          <rect
                            x={bx + bw + 2}
                            y={143 - ((d.autopilot + d.person) / activityMax) * 125}
                            width={bw}
                            height={(d.person / activityMax) * 125}
                            rx="2"
                            fill="var(--viz-sessions)"
                          />
                        </g>
                        {(i % Math.ceil(daily.length / 5) === 0 || i === daily.length - 1) && (
                          <text
                            x={bx + bw}
                            y="168"
                            textAnchor="middle"
                            fontSize="10"
                            fill="currentColor"
                            className="text-muted-foreground"
                          >
                            {shortDay(d.day).replace(/^\w+, /, '')}
                          </text>
                        )}
                      </g>
                    )
                  })}
                </svg>
              ) : (
                <p className="py-12 text-sm text-muted-foreground">
                  {operations.isLoading
                    ? 'Loading decisions…'
                    : 'No daily decision observations in this period.'}
                </p>
              )}
              <select
                aria-label="Inspect a decision day"
                value={activity ?? ''}
                onChange={(e) => setActivity(e.target.value === '' ? null : Number(e.target.value))}
                className="mt-2 rounded-md border bg-background px-2 py-1.5 text-xs"
              >
                <option value="">Inspect a day…</option>
                {daily.map((d, i) => (
                  <option key={d.day} value={i}>
                    {shortDay(d.day)}
                  </option>
                ))}
              </select>
              <p className="mt-3 text-xs text-muted-foreground">
                Acceptance is a decision; operations tracks application and real examples.
              </p>
            </>
          )}
        </div>

        <div
          className="viz-rise rounded-2xl border bg-card p-5 md:p-6"
          style={{ animationDelay: '200ms' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-serif text-2xl">What the garden knows</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Concepts by ontology category · current graph
              </p>
            </div>
            <Link
              to="/app/knowledge/explore"
              className="rounded p-1 hover:bg-muted"
              aria-label="Explore ontology"
            >
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </div>
          {overview.isError ? (
            <p role="alert" className="mt-8 text-sm">
              Ontology distribution could not be loaded.{' '}
              <button className="underline" onClick={() => overview.refetch()}>
                Retry
              </button>
            </p>
          ) : total ? (
            <>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-5 sm:flex-nowrap">
                <div className="relative h-52 w-52 shrink-0">
                  <svg
                    viewBox="0 0 208 208"
                    className="h-full w-full -rotate-90"
                    role="img"
                    aria-label="Ontology distribution. Choose a category below to inspect its share."
                  >
                    {categories.map((c) => {
                      const share = c.concepts / total
                      const start = offset
                      offset += share
                      return (
                        <circle
                          key={c.category}
                          cx="104"
                          cy="104"
                          r="82"
                          pathLength="1"
                          strokeDasharray={`${share} ${1 - share}`}
                          strokeDashoffset={-start}
                          stroke={categoryColor(c.category)}
                          strokeWidth={category === c.category ? 27 : 19}
                          fill="none"
                          opacity={!category || category === c.category ? 1 : 0.25}
                          className="motion-safe:transition-all motion-safe:duration-500"
                          onPointerEnter={() => setCategory(c.category)}
                        >
                          <title>
                            {c.category}: {c.concepts.toLocaleString()} concepts
                          </title>
                        </circle>
                      )
                    })}
                  </svg>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-serif text-3xl">
                      <AnimatedNumber value={chosen?.concepts ?? total} />
                    </span>
                    <span className="max-w-28 text-center text-xs capitalize text-muted-foreground">
                      {chosen?.category ?? 'concepts'}
                    </span>
                    <span className="mt-1 text-xs text-muted-foreground">
                      {chosen
                        ? `${((chosen.concepts / total) * 100).toFixed(1)}% of graph`
                        : `${categories.length} categories`}
                    </span>
                  </div>
                </div>
                <div className="w-full space-y-1" role="group" aria-label="Ontology categories">
                  {(showAllCategories ? categories : categories.slice(0, 6)).map((c) => (
                    <button
                      key={c.category}
                      aria-pressed={category === c.category}
                      onClick={() => setCategory(category === c.category ? null : c.category)}
                      className={`w-full rounded-lg px-2 py-1.5 text-left transition-colors ${category === c.category ? 'bg-muted' : 'hover:bg-muted/60'}`}
                    >
                      <span className="flex items-center gap-2 text-xs">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: categoryColor(c.category) }}
                        />
                        <span className="flex-1 capitalize">{c.category || 'Uncategorised'}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {compactCount(c.concepts)}
                        </span>
                      </span>
                      <span className="mt-1 block h-1 overflow-hidden rounded-full bg-muted">
                        <span
                          className="viz-grow-x block h-full rounded-full"
                          style={{
                            width: `${(c.concepts / total) * 100}%`,
                            background: categoryColor(c.category),
                          }}
                        />
                      </span>
                    </button>
                  ))}
                  {categories.length > 6 && (
                    <button
                      className="px-2 pt-2 text-xs text-muted-foreground underline underline-offset-4"
                      aria-expanded={showAllCategories}
                      onClick={() => setShowAllCategories((v) => !v)}
                    >
                      {showAllCategories
                        ? 'Show fewer categories'
                        : `Show all ${categories.length} categories`}
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-3 min-h-5 text-xs text-muted-foreground">
                {chosen
                  ? `${chosen.artworkLinks.toLocaleString()} artwork links · ${chosen.merged.toLocaleString()} concepts with merged names`
                  : 'Choose a category to reveal its share and artwork connections.'}
              </p>
            </>
          ) : (
            <p className="py-20 text-center text-sm text-muted-foreground">
              {overview.isLoading ? 'Loading the ontology…' : 'No categories recorded yet.'}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
